import { Theme, ThemeVariant, ClientTheme, BrandPalette } from '@type/theme';
import type { OrgConfig } from '@/types/orgConfig';

// ===== Static theme objects (used by ThemeContext consumers reading variant) =====
export const lightTheme: Theme = {
  variant: 'light',
  colors: {
    background: {
      primary: 'var(--color-background-primary)',
      secondary: 'var(--color-background-secondary)',
      tertiary: 'var(--color-background-tertiary)',
    },
    text: {
      primary: 'var(--color-text-primary)',
      secondary: 'var(--color-text-secondary)',
      tertiary: 'var(--color-text-tertiary)',
      inverse: 'var(--color-text-inverse)',
    },
    border: {
      primary: 'var(--color-border-primary)',
      secondary: 'var(--color-border-secondary)',
      focus: 'var(--color-border-focus)',
    },
    interactive: {
      primary: 'var(--color-interactive-primary)',
      primaryHover: 'var(--color-interactive-primary-hover)',
      primaryLight: 'var(--color-interactive-primary-light)',
      secondary: 'var(--color-interactive-secondary)',
      secondaryHover: 'var(--color-interactive-secondary-hover)',
    },
    button: {
      primary: {
        background: 'var(--color-button-primary-background)',
        backgroundHover: 'var(--color-button-primary-background-hover)',
        text: 'var(--color-button-primary-text)',
        border: 'var(--color-button-primary-border)',
      },
      secondary: {
        background: 'var(--color-button-secondary-background)',
        backgroundHover: 'var(--color-button-secondary-background-hover)',
        text: 'var(--color-button-secondary-text)',
        border: 'var(--color-button-secondary-border)',
      },
    },
    status: {
      success: 'var(--color-status-success-text)',
      warning: 'var(--color-status-warning-text)',
      error: 'var(--color-status-error-text)',
      info: 'var(--color-status-info-text)',
    },
  },
  spacing: {
    xs: 'var(--spacing-xs)',
    sm: 'var(--spacing-sm)',
    md: 'var(--spacing-md)',
    lg: 'var(--spacing-lg)',
    xl: 'var(--spacing-xl)',
    xxl: 'var(--spacing-xxl)',
  },
  borderRadius: {
    sm: 'var(--border-radius-sm)',
    md: 'var(--border-radius-md)',
    lg: 'var(--border-radius-lg)',
    full: 'var(--border-radius-full)',
  },
  shadows: {
    sm: 'var(--shadow-sm)',
    md: 'var(--shadow-md)',
    lg: 'var(--shadow-lg)',
    xl: 'var(--shadow-xl)',
  },
};

export const darkTheme: Theme = { ...lightTheme, variant: 'dark' };

const themesByVariant = { light: lightTheme, dark: darkTheme } as const;

export const getTheme = (variant: ThemeVariant): Theme =>
  themesByVariant[variant];

// ===== DOM application =====

let activeClientTheme: ClientTheme | null = null;

const DYNAMIC_CSS_PROPS = [
  '--color-button-primary-background',
  '--color-button-primary-background-hover',
  '--color-button-primary-text',
  '--color-button-primary-border',
  '--color-button-secondary-text',
  '--color-button-secondary-border',
  '--color-interactive-primary',
  '--color-interactive-primary-hover',
  '--color-interactive-primary-light',
  '--color-border-focus',
];

const isDynamicTheme = (theme: ClientTheme): boolean =>
  theme.id.startsWith('dynamic-');

const applyPaletteToRoot = (palette: BrandPalette | null): void => {
  const root = document.documentElement;
  if (!palette) {
    DYNAMIC_CSS_PROPS.forEach(prop => root.style.removeProperty(prop));
    return;
  }
  root.style.setProperty(
    '--color-button-primary-background',
    palette.buttonPrimaryBg
  );
  root.style.setProperty(
    '--color-button-primary-background-hover',
    palette.buttonPrimaryBgHover
  );
  root.style.setProperty(
    '--color-button-primary-text',
    palette.buttonPrimaryText
  );
  root.style.setProperty(
    '--color-button-primary-border',
    palette.buttonPrimaryBorder
  );
  root.style.setProperty(
    '--color-button-secondary-text',
    palette.buttonSecondaryText
  );
  root.style.setProperty(
    '--color-button-secondary-border',
    palette.buttonSecondaryBorder
  );
  root.style.setProperty(
    '--color-interactive-primary',
    palette.interactivePrimary
  );
  root.style.setProperty(
    '--color-interactive-primary-hover',
    palette.interactivePrimaryHover
  );
  root.style.setProperty('--color-border-focus', palette.borderFocus);
  const hex = palette.interactivePrimary.replace('#', '');
  if (/^[0-9a-fA-F]{6}$/.test(hex)) {
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    root.style.setProperty(
      '--color-interactive-primary-light',
      `rgba(${r}, ${g}, ${b}, 0.1)`
    );
  }
};

export const applyTheme = (variant: ThemeVariant): void => {
  document.documentElement.setAttribute('data-theme', variant);
  if (activeClientTheme && isDynamicTheme(activeClientTheme)) {
    applyPaletteToRoot(activeClientTheme.palette[variant]);
  }
};

export const applyClientTheme = (clientTheme: ClientTheme | null): void => {
  const root = document.documentElement;
  activeClientTheme = clientTheme;
  if (!clientTheme) {
    root.removeAttribute('data-client-theme');
    applyPaletteToRoot(null);
    return;
  }
  root.setAttribute('data-client-theme', clientTheme.id);
  if (isDynamicTheme(clientTheme)) {
    const variant =
      (root.getAttribute('data-theme') as ThemeVariant) || 'light';
    applyPaletteToRoot(clientTheme.palette[variant]);
  } else {
    applyPaletteToRoot(null);
  }
};

// ===== System preference =====
export const getSystemTheme = (): ThemeVariant => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return 'light';
};

// ===== localStorage =====
const THEME_KEY = 'theme';
const CLIENT_THEME_KEY = 'clientTheme';

export const getStoredTheme = (): ThemeVariant | null => {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  const stored = localStorage.getItem(THEME_KEY);
  return stored === 'dark' || stored === 'light' ? stored : null;
};

export const storeTheme = (variant: ThemeVariant): void => {
  if (typeof window !== 'undefined' && window.localStorage) {
    localStorage.setItem(THEME_KEY, variant);
  }
};

export const getStoredClientTheme = (): ClientTheme | null => {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  const stored = localStorage.getItem(CLIENT_THEME_KEY);
  if (!stored) return null;
  // Stored value is just the id; resolve the live ClientTheme.
  // Avoids serializing the Logo component reference.
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { getOperatorTheme } = require('./operatorThemes');
  return getOperatorTheme(stored) ?? null;
};

export const storeClientTheme = (clientTheme: ClientTheme | null): void => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  if (clientTheme) {
    localStorage.setItem(CLIENT_THEME_KEY, clientTheme.id);
  } else {
    localStorage.removeItem(CLIENT_THEME_KEY);
  }
};

// Last-known-good org branding (colours + logo URLs). Cached as raw JSON so the dynamic theme can
// be regenerated and applied on the next load before the CMS fetch resolves — avoids the
// default-palette flash. The dynamic ClientTheme itself isn't serialisable (Logo component).
//
// Two layers:
//  - per-org key `cachedOrgBranding:{orgId}` — so switching to a previously-visited store shows the
//    right branding instantly instead of the previous org's.
//  - the bare `cachedOrgBranding` key — a pointer to the last-applied branding, read by the pre-bundle
//    inline script in index.html and by ThemeContext's seed (which don't know the orgId yet).
//
// NOTE: index.html's inline script hard-codes the same palette mapping as generateDynamicTheme /
// applyPaletteToRoot. Keep them in sync if the palette derivation changes.
const ORG_BRANDING_KEY = 'cachedOrgBranding';
const orgKey = (orgId: number): string => `${ORG_BRANDING_KEY}:${orgId}`;

export const getStoredOrgBranding = (orgId?: number | null): OrgConfig | null => {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  const key = orgId != null ? orgKey(orgId) : ORG_BRANDING_KEY;
  const stored = localStorage.getItem(key);
  if (!stored) return null;
  try {
    return JSON.parse(stored) as OrgConfig;
  } catch {
    return null;
  }
};

export const storeOrgBranding = (branding: OrgConfig | null): void => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  if (!branding) {
    localStorage.removeItem(ORG_BRANDING_KEY);
    return;
  }
  localStorage.setItem(orgKey(branding.orgId), JSON.stringify(branding));
  localStorage.setItem(ORG_BRANDING_KEY, JSON.stringify(branding));
};

/** Drop a specific org's cached branding (e.g. when CMS reports it's no longer SMP-enabled). */
export const clearStoredOrgBranding = (orgId: number): void => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  localStorage.removeItem(orgKey(orgId));
  const current = getStoredOrgBranding();
  if (current?.orgId === orgId) {
    localStorage.removeItem(ORG_BRANDING_KEY);
  }
};
